import React, { useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Cliente from "./Cliente";
import AdicionarLicenca from "./AdicionarLicenca";
import ListarLicencas from "./ListarLicencas";
import ListarPagamentos from "./ListarPagamentos";
import ResetSenhaUser from "./ResetSenhaUser";
import Suporte from "./Suporte";
import Downloads from "./Downloads";
import LanguageSwitcher from "../LanguageSwitcher";

import { useAuth } from "../auth";
import api from "../api";
import "./estilodashuser.css";

export default function UserDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, token, logout } = useAuth();

  const [nomeCliente, setNomeCliente] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [carregandoNome, setCarregandoNome] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !token || !user) {
      navigate("/login", { replace: true });
      return;
    }
    fetchClienteNome(user.id);
  }, [isAuthenticated, token, user, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const fetchClienteNome = async (userId) => {
    try {
      setCarregandoNome(true);
      const response = await api.get(`/api/clientes/${userId}`);
      setNomeCliente(response.data?.nome || t('dashboard.customer'));
    } catch (error) {
      console.error("Erro ao buscar nome do cliente:", error);
      setNomeCliente(t('dashboard.customer'));
    } finally {
      setCarregandoNome(false);
    }
  };

  const handleLogout = () => {
    try {
      if (typeof logout === "function") logout();
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    } catch (e) {
      console.error("Erro ao fazer logout:", e);
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const menuItems = useMemo(
    () => [
      {
        path: "/dashboard-cliente/cliente",
        icon: "👤",
        label: t('dashboard.menu.account.label'),
        help: t('dashboard.menu.account.help'),
      },
      {
        path: "/dashboard-cliente/adicionar-licenca",
        icon: "➕",
        label: t('dashboard.menu.addLicense.label'),
        help: t('dashboard.menu.addLicense.help'),
      },
      {
        path: "/dashboard-cliente/listar-licencas",
        icon: "📜",
        label: t('dashboard.menu.licenses.label'),
        help: t('dashboard.menu.licenses.help'),
      },
      {
        path: "/dashboard-cliente/listar-pagamentos",
        icon: "💳",
        label: t('dashboard.menu.payments.label'),
        help: t('dashboard.menu.payments.help'),
      },
      {
        path: "/dashboard-cliente/downloads",
        icon: "📥",
        label: t('dashboard.menu.downloads.label'),
        help: t('dashboard.menu.downloads.help'),
      },
      {
        path: "/dashboard-cliente/suporte",
        icon: "📱",
        label: t('dashboard.menu.support.label'),
        help: t('dashboard.menu.support.help'),
      },
      {
        path: "/dashboard-cliente/reset-senha",
        icon: "🔒",
        label: t('dashboard.menu.password.label'),
        help: t('dashboard.menu.password.help'),
      },
    ],
    [t]
  );

  const currentMenuItem = useMemo(() => {
    return menuItems.find((item) => location.pathname === item.path) || null;
  }, [menuItems, location.pathname]);

  const isActive = (path) => location.pathname === path;

  const goTo = (path) => {
    navigate(path);
  };

  return (
    <div className="dash-shell">
      {sidebarOpen && (
        <div
          className="dash-overlay"
          onClick={() => setSidebarOpen(false)}
          role="button"
          tabIndex={0}
          aria-label={t('dashboard.closeMenu')}
        />
      )}

      <aside className={`dash-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="dash-brand">
          <div className="dash-brand-title">Quantum Trades</div>
          <div className="dash-brand-subtitle">{t('dashboard.customerArea')}</div>
        </div>

        <nav className="dash-menu">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`dash-menu-item ${isActive(item.path) ? "active" : ""}`}
              onClick={() => goTo(item.path)}
              type="button"
              title={item.help}
            >
              <span className="dash-menu-ico">{item.icon}</span>
              <span className="dash-menu-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="dash-sidebar-footer">
          <button className="dash-logout" onClick={handleLogout} type="button">
            <span className="dash-menu-ico">🚪</span>
            <span>{t('dashboard.logout')}</span>
          </button>
        </div>
      </aside>

      <main className="dash-main">
        <header className="dash-topbar">
          <button
            className="dash-burger"
            onClick={() => setSidebarOpen((v) => !v)}
            type="button"
            aria-label={t('dashboard.openMenu')}
          >
            ☰
          </button>

          <div className="dash-topbar-title">
            <div className="dash-welcome">
              {carregandoNome
                ? t('dashboard.loadingArea')
                : t('dashboard.welcome', { name: nomeCliente || t('dashboard.customer') })}
            </div>
            <div className="dash-welcome-sub">
              {currentMenuItem?.help ||
                t('dashboard.defaultHelp')}
            </div>
          </div>
          <LanguageSwitcher className="dashboard-language-switcher" />
        </header>

        <section className="dash-content">
          <div className="user-orientation-card">
            <div className="user-orientation-title">{t('dashboard.quickTitle')}</div>
            <div className="user-orientation-text">{t('dashboard.quickText')}</div>
          </div>

          <Routes>
            <Route path="/" element={<Navigate to="cliente" replace />} />
            <Route path="cliente" element={<Cliente userId={user?.id} />} />
            <Route path="adicionar-licenca" element={<AdicionarLicenca userId={user?.id} />} />
            <Route path="listar-licencas" element={<ListarLicencas userId={user?.id} />} />
            <Route path="listar-pagamentos" element={<ListarPagamentos userId={user?.id} />} />
            <Route path="downloads" element={<Downloads userId={user?.id} />} />
            <Route path="suporte" element={<Suporte userId={user?.id} />} />
            <Route path="reset-senha" element={<ResetSenhaUser userId={user?.id} />} />
            <Route path="*" element={<Navigate to="cliente" replace />} />
          </Routes>
        </section>
      </main>
    </div>
  );
}
